import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grow,
  Link,
  Slider,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CitationHelpDialog from '../components/CitationHelpDialog';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { getFactCheckItem } from '../services/factCheckItemService';
import { getValidPdfUrl, getValidVideoUrl } from '../services/gcs';
import { listenToProcessingStatus } from '../services/statusListener';
import { IMG } from '../theme/assets';
import { VERDICT_LABELS, verdictKeyFromLabel, verdictSealFromLabel } from '../theme/designTokens';
import type {
  ExtractedFact,
  FactCheckItem,
  IndexedVerificationResult,
  VerificationSourceData,
} from '../types';
import { getQuote } from '../utils/quote';
import { calculateVerificationStats } from '../utils/statsUtils';

const ProcessingResults: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // テーマカラー変数
  const panelBg = themeMode === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.85)';
  const borderColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const textPrimary = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textSecondary = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  const textMuted = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)';
  const cardBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
  const evidenceBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';
  const evidenceText = themeMode === 'dark' ? '#cbd5e1' : '#475569';
  const labelText = themeMode === 'dark' ? '#e2e8f0' : '#374151';
  const subtleText = themeMode === 'dark' ? '#94a3b8' : '#6b7280';
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const accordionBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)';
  const speedControlBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';

  // 案件からの遷移の場合、戻り先を案件に設定
  const fromWorkspace = (location.state as any)?.fromWorkspace;
  const backPath = fromWorkspace ? `/share/${fromWorkspace}` : '/';
  const backLabel = fromWorkspace ? '案件に戻る' : 'マイページに戻る';

  // 状態管理
  const [item, setItem] = useState<FactCheckItem | null>(null);
  const [originalFacts, setOriginalFacts] = useState<ExtractedFact[]>([]);
  const [selectedFacts, setSelectedFacts] = useState<ExtractedFact[]>([]); // 選択されたファクトのみ
  const [verificationResults, setVerificationResults] = useState<IndexedVerificationResult[]>([]);
  const [newlyCompletedIndex, setNewlyCompletedIndex] = useState<number>(-1);
  const [isProcessing, setIsProcessing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // 最新の状態をリスナー内で参照するための ref
  const verificationResultsRef = useRef<IndexedVerificationResult[]>([]);
  const itemRef = useRef<FactCheckItem | null>(null);
  const selectedFactsRef = useRef<ExtractedFact[]>([]);

  // ref を最新の状態に同期
  useEffect(() => {
    verificationResultsRef.current = verificationResults;
  }, [verificationResults]);

  useEffect(() => {
    itemRef.current = item;
  }, [item]);

  useEffect(() => {
    selectedFactsRef.current = selectedFacts;
  }, [selectedFacts]);

  // 動画対応用の状態
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);

  // 動画タイプ判定
  const isVideoItem = item?.type === 'video_upload' || item?.type === 'video_url';

  // 初期化：アイテムと元のファクト配列を取得
  useEffect(() => {
    const loadItem = async () => {
      if (!sessionId || !user) return;

      try {
        // location.stateから初期データを取得するか、Firestoreから取得
        // Firestoreから直接取得（factcheck_itemsコレクション）
        const foundItem = await getFactCheckItem(sessionId);
        if (foundItem) {
          // PDF URLの期限切れチェック & 自動更新
          try {
            const validPdfUrl = await getValidPdfUrl(sessionId, foundItem.fileUrl || '');
            const updatedItem = { ...foundItem, fileUrl: validPdfUrl };
            setItem(updatedItem);
          } catch (error) {
            console.error('PDF URL更新エラー:', error);
            setItem(foundItem);
          }

          setOriginalFacts(foundItem.facts || []);

          // 選択されたファクトのみを取得
          const selectedIndices = foundItem.selectedFactIndices || [];
          if (selectedIndices.length > 0) {
            const selectedFactsList = selectedIndices
              .map((index) => foundItem.facts[index])
              .filter(Boolean);
            setSelectedFacts(selectedFactsList);
          } else {
            setSelectedFacts(foundItem.facts || []);
          }

          // 既存の検証結果があれば読み込み
          if (foundItem.verification_results && foundItem.verification_results.length > 0) {
            const indexedResults = foundItem.verification_results.map((result, index) => ({
              ...result,
              originalIndex: index,
              factId: `fact-${index}`,
              completedAt: new Date().toISOString(),
            })) as IndexedVerificationResult[];
            setVerificationResults(indexedResults);
            const expectedCount =
              selectedIndices.length > 0 ? selectedIndices.length : foundItem.facts.length;
            if (foundItem.verification_results.length === expectedCount) {
              setIsProcessing(false);
            }
          }
        }
      } catch (error) {
        console.error('アイテム読み込みエラー:', error);
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [sessionId, user]);

  // バックグラウンドでのFirestoreリアルタイム監視
  useEffect(() => {
    if (!sessionId || !isProcessing || !user) return;

    console.log(`📡 Firestoreリスナー登録: ${sessionId}`);

    const handleStatusUpdate = async (statusData: any) => {
      const currentResults =
        (statusData.results_array.filter((r: any) => !!r) as IndexedVerificationResult[]) || [];

      // 前回の結果と比較して新しく完了したものを検出
      const prevResults = verificationResultsRef.current;
      const newResult = currentResults.find(
        (result) =>
          !prevResults.some((existing) => existing.originalIndex === result.originalIndex),
      );

      if (newResult) {
        setNewlyCompletedIndex(newResult.originalIndex);
        // 3秒後にハイライトを解除
        setTimeout(() => setNewlyCompletedIndex(-1), 3000);

        // 通知と完了処理
        const currentItem = itemRef.current;
        if (currentItem?.title) {
          const completedCount = currentResults.length;
          const expectedTotalCount = selectedFactsRef.current.length;

          if (completedCount === expectedTotalCount && expectedTotalCount > 0) {
            console.log('✅ 全ファクトチェック完了:', currentItem.title);

            // 最終結果はバックエンド側で自動保存され、サブコレクションに格納されているため
            // フロントエンドではステータスのみ更新して遷移
            const updatedItem = { ...currentItem, status: 'completed' as const };
            setItem(updatedItem);

            // 完了したら2秒後に結果画面に遷移
            setTimeout(() => {
              navigate(`/results/${sessionId}`, {
                state: { item: updatedItem },
              });
            }, 2000);
          }
        }
      }

      setVerificationResults(currentResults);

      // 完了ステータスになったら処理を終了（リスナー停止）
      if (statusData.status === 'completed') {
        setIsProcessing(false);
      }
    };

    // Firestoreリスナー開始
    const unsubscribe = listenToProcessingStatus(sessionId, handleStatusUpdate);

    return () => {
      console.log(`⏹️ Firestoreリスナー解除: ${sessionId}`);
      unsubscribe();
    };
    // 依存配列から verificationResults と item を削除し、再登録を抑制
  }, [sessionId, isProcessing, user, navigate]);

  // 動画URLの取得
  useEffect(() => {
    const loadVideoUrl = async () => {
      if (!sessionId || !item || !isVideoItem) return;

      try {
        if (item.blobName) {
          const validUrl = await getValidVideoUrl(sessionId, item.fileUrl || '', item.blobName);
          setVideoUrl(validUrl);
        }
      } catch (error) {
        console.error('動画URL取得エラー:', error);
      }
    };

    loadVideoUrl();
  }, [sessionId, item, isVideoItem]);

  // 再生速度の変更
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // 結果を元の順序で取得
  const getResultByIndex = (index: number): IndexedVerificationResult | null => {
    return (
      verificationResults
        .filter((result) => !!result)
        .find((result) => result.originalIndex === index) || null
    );
  };

  // 判定結果のラベルを取得（状態に正規化してから表示名を引く）
  const getJudgementLabel = (judgement: string) => VERDICT_LABELS[verdictKeyFromLabel(judgement)];

  // 結果データの解析
  const parseResultSource = (source: string): VerificationSourceData | null => {
    try {
      return JSON.parse(source);
    } catch (e) {
      console.error('結果データの解析エラー:', e);
      return null;
    }
  };

  // 統計の計算（内訳と合計がずれないよう共通の集計に寄せる）
  const calculateStats = () => calculateVerificationStats(verificationResults);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item || selectedFacts.length === 0) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 3 }}>
          {backLabel}
        </Button>
        <Alert severity="error">プロジェクトが見つかりませんでした。</Alert>
      </Box>
    );
  }

  const stats = calculateStats();

  return (
    <Box sx={{ width: '100%', height: 'calc(var(--app-vh) - 110px)', overflow: 'hidden' }}>
      {/* ヘッダー（mock .rhead: 帯なし） */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          px: 1.5,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{
            background: themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)',
            border: `1px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 0.9,
            fontSize: 13,
            fontWeight: 700,
            color: textPrimary,
            textTransform: 'none',
            '&:hover': { background: themeMode === 'dark' ? 'rgba(255,255,255,0.14)' : '#fff' },
          }}
        >
          {backLabel}
        </Button>

        <Typography sx={{ fontSize: 21, fontWeight: 900, color: textPrimary }}>
          ファクトチェック実行中:{' '}
          <Box component="span" sx={{ color: tokens.teal }}>
            {item.title}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<InfoIcon />}
            onClick={() => setShowLocationModal(true)}
            sx={{
              background: themeMode === 'dark' ? 'rgba(129,140,248,0.12)' : '#eef0ff',
              border: `1px solid ${themeMode === 'dark' ? 'rgba(129,140,248,0.3)' : '#d9ddff'}`,
              borderRadius: '11px',
              px: 1.5,
              py: 0.8,
              fontSize: 12.5,
              fontWeight: 800,
              color: tokens.indigo,
              textTransform: 'none',
              '&:hover': {
                background: themeMode === 'dark' ? 'rgba(129,140,248,0.18)' : '#e4e7ff',
              },
            }}
          >
            引用の確認方法
          </Button>
          <Button
            onClick={() => navigate(`/results/${sessionId}`, { state: { item } })}
            disabled={verificationResults.length !== selectedFacts.length}
            sx={{
              background: tokens.teal,
              borderRadius: '11px',
              px: 1.75,
              py: 0.8,
              fontSize: 12.5,
              fontWeight: 800,
              color: '#fff',
              textTransform: 'none',
              '&:hover': { background: tokens.teal, filter: 'brightness(0.95)' },
              '&.Mui-disabled': { background: tokens.line, color: tokens.faint },
            }}
          >
            結果を見る
          </Button>
        </Box>
      </Box>

      {/* メインコンテンツ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          height: 'calc(100% - 64px)',
          minHeight: 0,
          overflow: 'hidden',
          p: 1.5,
        }}
      >
        {/* 左パネル - 検証結果（順次更新・mock: 外枠なし） */}
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 進捗バナー（mock .prog） */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.25,
              flexWrap: 'wrap',
              background:
                themeMode === 'dark'
                  ? 'linear-gradient(120deg, rgba(102,126,234,0.16), rgba(13,148,136,0.14))'
                  : 'linear-gradient(120deg, #eef5ff, #eafaf6)',
              border: `1px solid ${tokens.line}`,
              borderRadius: '13px',
              px: 2.5,
              py: 2,
              boxShadow: '0 8px 24px rgba(30,30,70,0.07)',
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={IMG.owl.clipboard}
              alt=""
              sx={{
                width: 64,
                height: 64,
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'drop-shadow(0 6px 12px rgba(30,30,70,0.16))',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {isProcessing ? (
                  <Box
                    sx={{
                      width: 17,
                      height: 17,
                      border: `3px solid ${tokens.teal}40`,
                      borderTopColor: tokens.teal,
                      borderRadius: '50%',
                      animation: 'prspin 0.9s linear infinite',
                      '@keyframes prspin': { to: { transform: 'rotate(360deg)' } },
                    }}
                  />
                ) : (
                  <CheckIcon sx={{ fontSize: 20, color: tokens.verdict.accurate.color }} />
                )}
                <Typography sx={{ fontSize: 17, fontWeight: 900, color: textPrimary }}>
                  {isProcessing ? 'ファクトチェック中…' : '検証が完了しました！'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: textMuted, mt: 0.75, mb: 1 }}>
                <Box component="span" sx={{ color: textPrimary, fontWeight: 900, fontSize: 16 }}>
                  {verificationResults.length}
                </Box>
                {' / '}
                {selectedFacts.length} 件 検証完了（残り{' '}
                {Math.max(0, selectedFacts.length - verificationResults.length)} 件）
              </Typography>
              <Box
                sx={{ height: 11, borderRadius: '7px', background: '#e3e7ef', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${selectedFacts.length ? Math.round((verificationResults.length / selectedFacts.length) * 100) : 0}%`,
                    background: `linear-gradient(90deg, ${tokens.teal}, #34d399)`,
                    borderRadius: '7px',
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, flexShrink: 0 }}>
              {[
                { label: '正確', count: stats.accurate, vkey: 'accurate' },
                { label: '部分的', count: stats.partiallyAccurate, vkey: 'partial' },
                { label: '不正確', count: stats.inaccurate, vkey: 'inaccurate' },
                { label: '検証不可', count: stats.unverifiable, vkey: 'unverifiable' },
                // 失敗したものがあるときだけ出す。黙って消すと内訳と合計が合わない
                ...(stats.error > 0
                  ? [{ label: 'エラー', count: stats.error, vkey: 'error' }]
                  : []),
              ].map((s) => {
                const v = tokens.verdict[s.vkey as keyof typeof tokens.verdict];
                return (
                  <Box
                    key={s.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      background: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff',
                      border: `1px solid ${tokens.line}`,
                      borderRadius: '11px',
                      px: 1.25,
                      py: 0.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      component="img"
                      src={IMG.verdict[s.vkey as keyof typeof IMG.verdict]}
                      alt=""
                      sx={{ width: 22, height: 22, objectFit: 'contain' }}
                    />
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: textSecondary }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: v.color }}>
                      {s.count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 900,
              color: textMuted,
              px: 0.5,
              pb: 1.5,
              flexShrink: 0,
            }}
          >
            検証結果（順次更新されます）
          </Typography>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, pr: 1 }}>
            {/* 選択されたファクトのみ表示 */}
            {selectedFacts.map((fact, displayIndex) => {
              // 元の配列でのインデックスを取得
              const originalIndex = originalFacts.findIndex(
                (originalFact) =>
                  originalFact.claim === fact.claim &&
                  originalFact.page === fact.page &&
                  originalFact.line_start === fact.line_start,
              );
              const result = getResultByIndex(originalIndex);
              const isNewlyCompleted = originalIndex === newlyCompletedIndex;

              return (
                <Card
                  key={`fact-${displayIndex}`}
                  sx={{
                    mb: 2,
                    background: themeMode === 'dark' ? cardBg : tokens.paper,
                    border: `1px solid ${borderColor}`,
                    borderLeft: '5px solid',
                    borderLeftColor: result
                      ? tokens.verdict[verdictKeyFromLabel(result.result)].color
                      : '#cbd2e0',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    boxShadow:
                      themeMode === 'dark'
                        ? '0 4px 6px rgba(0,0,0,0.1)'
                        : '0 5px 16px rgba(30,30,70,0.05)',
                    transition: 'all 0.5s ease',
                    transform: isNewlyCompleted ? 'scale(1.01)' : 'scale(1)',
                    ...(isNewlyCompleted && {
                      boxShadow: `0 0 18px ${tokens.verdict.accurate.color}66`,
                    }),
                    // ホバーで紫にしない（グローバルMuiCardのデフォルトを打ち消す。色は付けず影のみ）
                    '&:hover': {
                      transform: isNewlyCompleted ? 'scale(1.01)' : 'translateY(-2px)',
                      backgroundColor: themeMode === 'dark' ? cardBg : tokens.paper,
                      borderColor: borderColor,
                      borderLeftColor: result
                        ? tokens.verdict[verdictKeyFromLabel(result.result)].color
                        : '#cbd2e0',
                      boxShadow:
                        themeMode === 'dark'
                          ? '0 8px 20px rgba(0,0,0,0.28)'
                          : '0 10px 24px rgba(30,30,70,0.12)',
                    },
                  }}
                >
                  <CardContent>
                    {/* 判定ヘッダー（シール＋判定ピル / 検証中スピナー） */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                      {result ? (
                        <>
                          <Box
                            component="img"
                            src={verdictSealFromLabel(result.result)}
                            alt=""
                            sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }}
                          />
                          {(() => {
                            const v = tokens.verdict[verdictKeyFromLabel(result.result)];
                            return (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 11.5,
                                  fontWeight: 900,
                                  px: 1.1,
                                  py: '2px',
                                  borderRadius: 999,
                                  bgcolor: v.tint,
                                  color: v.color,
                                }}
                              >
                                {getJudgementLabel(result.result)}
                              </Box>
                            );
                          })()}
                        </>
                      ) : (
                        <>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              flexShrink: 0,
                              display: 'grid',
                              placeItems: 'center',
                              background: '#eef1f5',
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '3px solid rgba(100,116,139,0.3)',
                                borderTopColor: '#64748b',
                                borderRadius: '50%',
                                animation: 'prspin 0.9s linear infinite',
                              }}
                            />
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 800,
                              px: 1.1,
                              py: '2px',
                              borderRadius: 999,
                              bgcolor: '#eef1f5',
                              color: '#64748b',
                            }}
                          >
                            検証中…
                          </Box>
                        </>
                      )}
                      <Typography sx={{ ml: 'auto', fontSize: 11, color: textMuted }}>
                        {displayIndex + 1}/{selectedFacts.length}
                      </Typography>
                    </Box>

                    {/* ファクト内容 */}
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: textPrimary }}
                    >
                      {fact.claim}
                    </Typography>

                    {/* 詳細結果表示（完了時のみ） */}
                    {result ? (
                      <Grow in={true} timeout={800}>
                        <Box>
                          {/* 引用 */}
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: labelText, mb: 0.5 }}
                            >
                              引用
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                backgroundColor: evidenceBg,
                                p: 1,
                                borderRadius: 1,
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                color: evidenceText,
                                border: `1px solid ${borderColor}`,
                              }}
                            >
                              {getQuote(result) || `${result.claim.substring(0, 100)}...`}
                            </Typography>
                          </Box>

                          {/* 要約と詳細情報 */}
                          {(() => {
                            const resultData = parseResultSource(result.source);
                            return resultData ? (
                              <Box>
                                {/* 要約 */}
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: labelText, mb: 0.5 }}
                                  >
                                    要約
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.875rem',
                                      lineHeight: 1.6,
                                      color: evidenceText,
                                    }}
                                  >
                                    {resultData.summary}
                                  </Typography>
                                </Box>

                                {/* 詳細情報（アコーディオン） */}
                                <Accordion
                                  sx={{
                                    boxShadow: 'none',
                                    border: `1px solid ${borderColor}`,
                                    backgroundColor: accordionBg,
                                    '&:before': { display: 'none' },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: textSecondary }} />}
                                    sx={{
                                      minHeight: 'auto',
                                      py: 0.5,
                                      outline: 'none',
                                      '& .MuiAccordionSummary-content': { margin: '4px 0' },
                                      '&.Mui-focusVisible': {
                                        backgroundColor: 'transparent !important',
                                      },
                                      '&:focus, &:focus-visible': {
                                        outline: 'none',
                                        boxShadow: 'none',
                                      },
                                      '& .MuiTouchRipple-root': { display: 'none' },
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        color: labelText,
                                      }}
                                    >
                                      詳しく見る
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{
                                      pt: 1,
                                      pb: 1.5,
                                      px: 1.75,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1.75,
                                    }}
                                  >
                                    {/* 詳細な理由 */}
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 11,
                                          fontWeight: 800,
                                          color: tokens.teal,
                                          letterSpacing: '.02em',
                                          mb: 0.6,
                                        }}
                                      >
                                        詳細な理由
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 12.5,
                                          lineHeight: 1.6,
                                          color: evidenceText,
                                        }}
                                      >
                                        {resultData.reason}
                                      </Typography>
                                    </Box>

                                    {/* 出典が無い場合は、その理由が分かるようにする */}
                                    {(!resultData.sources || resultData.sources.length === 0) && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: evidenceText,
                                            letterSpacing: '.02em',
                                            mb: 0.5,
                                          }}
                                        >
                                          情報源
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            color: evidenceText,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {(result.method || '').includes('internal_knowledge')
                                            ? 'AIの知識のみで検証したため、参照元はありません。'
                                            : 'AIが検索不要と判断したため、参照元はありません。'}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* 情報源（検索で実際に参照したページ） */}
                                    {resultData.sources && resultData.sources.length > 0 && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: tokens.teal,
                                            letterSpacing: '.02em',
                                            mb: 0.75,
                                          }}
                                        >
                                          情報源
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.6,
                                          }}
                                        >
                                          {resultData.sources.map((source, sourceIndex) => (
                                            <Box
                                              key={sourceIndex}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 0.75,
                                                fontSize: 12,
                                                lineHeight: 1.5,
                                              }}
                                            >
                                              <LinkIcon
                                                sx={{
                                                  fontSize: 14,
                                                  color: accentColor,
                                                  mt: '2px',
                                                  flexShrink: 0,
                                                }}
                                              />
                                              {source.url ? (
                                                <Link
                                                  href={source.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  sx={{
                                                    textDecoration: 'none',
                                                    color: accentColor,
                                                    wordBreak: 'break-word',
                                                    '&:hover': { textDecoration: 'underline' },
                                                  }}
                                                >
                                                  {source.title}
                                                </Link>
                                              ) : (
                                                <Box component="span" sx={{ color: subtleText }}>
                                                  {source.title}
                                                </Box>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}

                                    {/* 思考プロセス（Google検索の場合のみ） */}
                                    {resultData.thought_process && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: tokens.teal,
                                            letterSpacing: '.02em',
                                            mb: 0.6,
                                          }}
                                        >
                                          思考プロセス
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap',
                                            color: subtleText,
                                            pl: 1.25,
                                            borderLeft: `2px solid ${borderColor}`,
                                          }}
                                        >
                                          {resultData.thought_process}
                                        </Typography>
                                      </Box>
                                    )}
                                  </AccordionDetails>
                                </Accordion>
                              </Box>
                            ) : (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                  sx={{ fontWeight: 600 }}
                                >
                                  ✅ 検証完了！
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      </Grow>
                    ) : (
                      <Box sx={{ py: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: textMuted,
                          }}
                        >
                          <CircularProgress size={12} />
                          このファクトは現在検証中です...
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* 右パネル - PDFプレビュー */}
        <Box
          sx={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: themeMode === 'dark' ? panelBg : tokens.paper,
            border: `1px solid ${tokens.line}`,
            borderRadius: '13px',
            boxShadow: themeMode === 'dark' ? 'none' : '0 8px 24px rgba(30,30,70,0.06)',
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 900,
              color: textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={isVideoItem ? IMG.preview.video : IMG.preview.document}
              alt=""
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
            {isVideoItem ? '動画プレビュー' : 'ドキュメントプレビュー'}
          </Typography>

          <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {isVideoItem ? (
              <>
                {/* 動画プレイヤー */}
                <Box
                  sx={{
                    flexGrow: 1,
                    minHeight: 220,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #1b2030, #39405a)',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'rgba(255,255,255,0.55)',
                  }}
                >
                  {videoUrl ? (
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{ width: 64, height: 64, fill: 'currentColor' }}
                    >
                      <path d="M8 5v14l11-7z" />
                    </Box>
                  )}
                </Box>

                {/* 再生速度スライダー */}
                <Box
                  sx={{
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor: speedControlBg,
                    borderRadius: 1,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SpeedIcon sx={{ fontSize: 18, color: textSecondary }} />
                    <Typography variant="body2" sx={{ color: textSecondary }}>
                      再生速度: {playbackRate}x
                    </Typography>
                  </Box>
                  <Slider
                    value={playbackRate}
                    onChange={(_, value) => setPlaybackRate(value as number)}
                    min={0.5}
                    max={3.0}
                    step={0.25}
                    marks={[
                      { value: 0.5, label: '0.5x' },
                      { value: 1.0, label: '1x' },
                      { value: 2.0, label: '2x' },
                      { value: 3.0, label: '3x' },
                    ]}
                    sx={{
                      color: accentColor,
                      '& .MuiSlider-markLabel': {
                        color: textMuted,
                        fontSize: '0.7rem',
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: accentColor,
                      },
                    }}
                  />
                </Box>
              </>
            ) : // PDFプレビュー
            item.fileUrl ? (
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <iframe
                  src={item.fileUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Preview"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 220,
                  borderRadius: '10px',
                  border: `1px solid ${tokens.line}`,
                  display: 'grid',
                  placeItems: 'center',
                  background: themeMode === 'dark' ? 'rgba(0,0,0,0.2)' : '#f6f9f8',
                }}
              >
                <Typography sx={{ color: textMuted, fontSize: 13 }}>
                  PDFプレビューが利用できません
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 処理中表示 - 処理中のみ表示 */}
      {isProcessing && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000, maxWidth: 400 }}>
          <Alert severity="info" sx={{ boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography>
                残り{selectedFacts.length - verificationResults.length}件を検証中...
              </Typography>
            </Box>
          </Alert>
        </Box>
      )}

      <CitationHelpDialog
        open={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        notice="完了後の結果画面で、各ファクトの引用文を確認できます"
      />
    </Box>
  );
};

export default ProcessingResults;
